import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { formatWallClockDateTime, isWallClockPast } from "../lib/wallClock";
import {
  Search, Loader2, ChevronDown, ChevronUp, ArrowRight,
  CalendarClock, Phone, StickyNote, CheckCircle2, Circle, Info,
} from "lucide-react";

const EMPTY_TITLE = "No matching portal referrals.";
const EMPTY_HINT =
  "This searches portal referrals only — patients who pre-date the portal or exist only in Raintree won't appear. Very recent referrals may take a few minutes to appear.";

const STATUS_STYLES = {
  cancelled: "bg-red-100 text-red-700",
  incomplete: "bg-amber-100 text-amber-700",
  prior_auth: "bg-orange-100 text-orange-700",
  complete_not_transferred: "bg-slate-200 text-slate-700",
  pending: "bg-blue-100 text-blue-700",
  crm_active: "bg-green-100 text-green-700",
  crm_closed: "bg-slate-100 text-slate-600",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// TRUE INSTANTS ONLY (callback_at, created_at…): converting to the viewer's
// zone is correct for these. Appointment stamps must NOT come through here —
// they are Pacific wall clock stored as UTC and go through
// `formatWallClockDateTime` (lib/wallClock.js). Rendering an appointment
// here is the 2026-09-01 bug: 7:00 AM showed as 2:00 AM on a Central
// browser, and early-morning stamps rolled back a day.
function fmtInstant(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function StatusBadge({ status }) {
  return (
    <span
      data-testid="search-status-badge"
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_STYLES[status.code] || "bg-slate-100 text-slate-600"}`}
    >
      {status.label}
    </span>
  );
}

function AppointmentCell({ datetime, location }) {
  if (!datetime) return null;
  // Wall clock vs wall clock — comparing this stamp to Date.now() would
  // skew the Past badge by the viewer's UTC offset.
  const isPast = isWallClockPast(datetime);
  return (
    <span
      data-testid="search-appointment"
      className={`inline-flex items-center gap-1 text-xs ${isPast ? "text-slate-400 line-through decoration-slate-300" : "text-purple-700"}`}
      title={isPast ? "Past appointment" : "Upcoming appointment"}
    >
      <CalendarClock className="h-3.5 w-3.5" />
      {formatWallClockDateTime(datetime)}
      {location ? ` · ${location}` : ""}
      {isPast && <span className="no-underline ml-1 rounded bg-slate-100 px-1 text-[10px] text-slate-500">Past</span>}
    </span>
  );
}

function ChecklistItem({ done, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
      {done ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <Circle className="h-3.5 w-3.5 text-slate-300" />}
      {label}
    </span>
  );
}

// Read-only summary card — deliberately no edit controls of any kind.
function SummaryCard({ summary }) {
  const s = summary;
  return (
    <div data-testid="patient-summary-card" className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={s.status} />
        {s.status.transferred && (
          <span data-testid="summary-transferred-badge" className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
            Transferred{s.transfer?.transferred_at ? ` ${fmtDate(s.transfer.transferred_at)}` : ""}
            {s.transfer?.transferred_by ? ` by ${s.transfer.transferred_by}` : ""}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
        <div><span className="text-slate-400 text-xs">DOB</span><div>{fmtDate(s.dob)}</div></div>
        <div><span className="text-slate-400 text-xs">Raintree PN</span><div>{s.pn || "—"}</div></div>
        <div><span className="text-slate-400 text-xs">Discipline</span><div>{s.discipline || "—"}</div></div>
        <div><span className="text-slate-400 text-xs">Referral Date</span><div>{fmtDate(s.referral_date)}</div></div>
        <div><span className="text-slate-400 text-xs">Location</span><div>{s.location || "—"}{s.location_detail ? ` · ${s.location_detail}` : ""}</div></div>
        <div><span className="text-slate-400 text-xs">File ID</span><div className="font-mono text-xs">{s.file_id}</div></div>
      </div>

      <div className="flex flex-wrap gap-4 border-t border-slate-200 pt-2" data-testid="summary-intake-checklist">
        <ChecklistItem done={s.intake_checklist.billing_complete} label="Billing" />
        <ChecklistItem done={s.intake_checklist.payor_complete} label="Payor" />
        <ChecklistItem done={s.intake_checklist.ready_for_crm} label="Ready for CRM" />
        {s.incomplete && (
          <span className="text-xs text-amber-700">Incomplete{s.incomplete_reason ? `: ${s.incomplete_reason}` : ""}</span>
        )}
        {s.needs_prior_auth && <span className="text-xs text-orange-700">Needs Prior Auth</span>}
      </div>

      {s.cancellation && (
        <div data-testid="summary-cancellation" className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          Cancelled {s.cancellation.cancelled_at ? fmtDate(s.cancellation.cancelled_at) : ""}
          {s.cancellation.cancelled_by ? ` by ${s.cancellation.cancelled_by}` : ""}
          {s.cancellation.reason ? ` — ${s.cancellation.reason}` : ""}
          {s.cancellation.notes ? ` (${s.cancellation.notes})` : ""}
        </div>
      )}

      {s.crm && (
        <div data-testid="summary-crm-block" className="space-y-1 border-t border-slate-200 pt-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">CRM Queue</div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-slate-400" />{s.crm.phone || "—"}</span>
            <span>Stage: <span className="font-medium">{s.crm.stage_name}</span></span>
            {s.crm.appointment_datetime && (
              <AppointmentCell datetime={s.crm.appointment_datetime} location={s.crm.appointment_location} />
            )}
            {s.crm.callback_at && (
              <span className="text-xs text-slate-500">
                Callback {fmtInstant(s.crm.callback_at)}{s.crm.callback_reason ? ` — ${s.crm.callback_reason}` : ""}
              </span>
            )}
          </div>
          {s.crm.notes && (
            <div className="flex items-start gap-1 rounded bg-white p-2 text-xs text-slate-600 border border-slate-200">
              <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <div>
                <div className="whitespace-pre-wrap">{s.crm.notes}</div>
                {s.crm.notes_updated_by && (
                  <div className="mt-1 text-[10px] text-slate-400">
                    {s.crm.notes_updated_by}{s.crm.notes_updated_at ? ` · ${fmtInstant(s.crm.notes_updated_at)}` : ""}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultRow({ row, expanded, summaryState, onToggle, onGoTo }) {
  return (
    <div className="border-t border-slate-100 first:border-t-0">
      <button
        type="button"
        data-testid={`search-result-row-${row.file_id}`}
        onClick={() => onToggle(row.file_id)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
      >
        <StatusBadge status={row.status} />
        <span className="text-xs text-slate-500">{row.discipline || "—"}</span>
        <span className="text-xs text-slate-500">Ref {fmtDate(row.referral_date)}</span>
        {row.pn && <span className="text-xs text-slate-500">PN {row.pn}</span>}
        {row.location && <span className="hidden text-xs text-slate-500 md:inline">{row.location}{row.location_detail ? ` · ${row.location_detail}` : ""}</span>}
        <AppointmentCell datetime={row.appointment_datetime} location={row.appointment_location} />
        <span className="ml-auto flex items-center gap-2">
          <span className="font-mono text-[10px] text-slate-400">{row.file_id}</span>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-3">
          {summaryState?.loading && (
            <div className="flex items-center gap-2 py-3 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading summary…
            </div>
          )}
          {summaryState?.error && (
            <div className="py-2 text-sm text-red-600" data-testid="summary-error">{summaryState.error}</div>
          )}
          {summaryState?.data && <SummaryCard summary={summaryState.data} />}
          <div className="mt-2">
            {row.link ? (
              <button
                type="button"
                data-testid={`go-to-patient-btn-${row.file_id}`}
                onClick={() => onGoTo(row.link)}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
              >
                Go to patient <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <span data-testid={`no-queue-note-${row.file_id}`} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <Info className="h-3.5 w-3.5" />
                {row.note || "Not visible in any queue — contact an admin to complete the transfer."}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PatientSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = nothing searched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [summaries, setSummaries] = useState({});
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setLoading(false);
      setError(null);
      return;
    }
    // ~300ms debounce before hitting the server
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/search", { params: { q } });
        setResults(res.data);
        setError(null);
      } catch (e) {
        setError(e.response?.data?.detail || "Search failed — please try again.");
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const toggleRow = useCallback((fileId) => {
    setExpandedRow((prev) => (prev === fileId ? null : fileId));
    setSummaries((prev) => {
      if (prev[fileId]?.data || prev[fileId]?.loading) return prev;
      api
        .get(`/search/${encodeURIComponent(fileId)}/summary`)
        .then((res) =>
          setSummaries((p) => ({ ...p, [fileId]: { data: res.data } }))
        )
        .catch((e) =>
          setSummaries((p) => ({
            ...p,
            [fileId]: { error: e.response?.data?.detail || "Could not load summary." },
          }))
        );
      return { ...prev, [fileId]: { loading: true } };
    });
  }, []);

  const goTo = useCallback((link) => navigate(link), [navigate]);

  const groups = results?.groups || [];
  const searched = results !== null && !loading;

  return (
    <div data-testid="patient-search-page" className="mx-auto max-w-5xl px-6 py-8">
      <h1 className="text-2xl font-semibold text-slate-800">Patient Search</h1>
      <p className="mt-1 text-sm text-slate-500">
        Find any patient across Intake and CRM by name, DOB, Raintree PN, phone, or file ID. Read-only.
      </p>

      <div className="relative mt-5">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="patient-search-input"
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, DOB (MM/DD/YYYY), PN, phone, or file ID…"
          className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm shadow-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />
        )}
      </div>

      {error && (
        <div data-testid="patient-search-error" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {searched && groups.length === 0 && !error && (
        <div data-testid="patient-search-empty" className="mt-10 text-center">
          <p className="text-sm font-medium text-slate-600">{EMPTY_TITLE}</p>
          <p className="mx-auto mt-2 max-w-lg text-xs text-slate-400">{EMPTY_HINT}</p>
        </div>
      )}

      {groups.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="patient-search-results">
          {results.truncated && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-700" data-testid="search-truncated-banner">
              Showing the first 25 matching patients — refine your search to narrow results.
            </div>
          )}
          {groups.map((g) => (
            <div key={`${g.name_norm}|${g.dob || ""}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="search-result-group">
              <div className="flex items-baseline gap-3 bg-slate-50 px-4 py-2">
                <span className="text-sm font-semibold text-slate-800">{g.display_name || "(No name)"}</span>
                <span className="text-xs text-slate-500">DOB {fmtDate(g.dob)}</span>
                {g.rows.length > 1 && (
                  <span className="text-[10px] text-slate-400">{g.rows.length} referrals</span>
                )}
              </div>
              {g.rows.map((row) => (
                <ResultRow
                  key={row.file_id}
                  row={row}
                  expanded={expandedRow === row.file_id}
                  summaryState={summaries[row.file_id]}
                  onToggle={toggleRow}
                  onGoTo={goTo}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
